/**
 * src/components/shared/BrainChipConfirm.jsx — TICKET-1499 (chat → brain).
 *
 * Under an agent reply: one row per brain chip the server saved from this turn,
 * "Saved to brain: <text> · Confirm / Discard". Confirm = accepted, Discard = rejected
 * (the quote generator then ignores it). Chips arrive on the SSE `brain_chips` event.
 */
import { useEffect, useState } from 'react';
import { Brain } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';
import { decideBrainChip, fetchBrainChipStatuses } from '../../lib/api/knowledge';
import { CHAT_CHIP_DECISIONS } from '../../lib/agent/brainChips';
import { logAppError } from '../../lib/errorReporter';

// ponytail: EN+ES as defaultValue because src/lib/i18n/{en,es}.json are file-locked by
// TICKET-1484; move these under `brainChat.*` in the json files when it lands.
const COPY = {
  en: { saved: 'Saved to brain:', confirm: 'Confirm', discard: 'Discard', confirmed: 'Confirmed', discarded: 'Discarded', failed: 'Could not save. Try again.' },
  es: { saved: 'Guardado en el cerebro:', confirm: 'Confirmar', discard: 'Descartar', confirmed: 'Confirmado', discarded: 'Descartado', failed: 'No se pudo guardar. Inténtalo de nuevo.' },
};

const rowStyle = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
  padding: '8px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.4,
  background: 'var(--glass)', border: '1px solid var(--bdr)', color: 'var(--t1)',
};
const btn = (primary, disabled) => ({
  minHeight: 44, padding: '8px 12px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
  cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
  background: primary ? 'var(--navy-surface)' : 'var(--glass)',
  color: primary ? 'var(--on-navy)' : 'var(--t1)',
  outline: primary ? 'none' : '1px solid var(--bdr)',
});

// stored status -> row state ('accepted' -> 'confirm', 'rejected' -> 'discard')
const DECIDED = Object.fromEntries(Object.entries(CHAT_CHIP_DECISIONS).map(([d, st]) => [st, d]));

function ChipRow({ chip, stored, copy, t }) {
  const [state, setState] = useState('proposed'); // proposed | busy | confirm | discard | error
  // After a reload the chip's real status wins: a decided chip never shows buttons again.
  useEffect(() => {
    if (DECIDED[stored]) setState((s) => (s === 'proposed' ? DECIDED[stored] : s));
  }, [stored]);
  const decide = async (decision) => {
    setState('busy');
    try {
      await decideBrainChip(chip.id, decision);
      setState(decision);
    } catch (err) {
      setState('error');
      logAppError({ type: 'brain_chip_decide_error', message: err?.message || String(err), view: 'AgentShell', metadata: { step: 'brain_chip_decide', chip_id: chip.id, decision } });
    }
  };
  const done = state === 'confirm' || state === 'discard';
  const busy = state === 'busy';
  return (
    <div style={rowStyle} data-testid="agent-brainchip-row">
      <Brain size={14} strokeWidth={2} style={{ color: 'var(--accent)', flexShrink: 0 }} />
      <span style={{ flex: '1 1 160px', minWidth: 0 }}>
        <strong>{t('brainChat.saved', { defaultValue: copy.saved })}</strong> {chip.text}
      </span>
      {done ? (
        <span data-testid="agent-brainchip-status" style={{ fontWeight: 600, color: state === 'confirm' ? 'var(--ok)' : 'var(--t2)' }}>
          {state === 'confirm' ? t('brainChat.confirmed', { defaultValue: copy.confirmed }) : t('brainChat.discarded', { defaultValue: copy.discarded })}
        </span>
      ) : (
        <span style={{ display: 'flex', gap: 8 }}>
          <button type="button" data-testid="agent-brainchip-confirm" disabled={busy} style={btn(true, busy)} onClick={() => decide('confirm')}>
            {t('brainChat.confirm', { defaultValue: copy.confirm })}
          </button>
          <button type="button" data-testid="agent-brainchip-discard" disabled={busy} style={btn(false, busy)} onClick={() => decide('discard')}>
            {t('brainChat.discard', { defaultValue: copy.discard })}
          </button>
        </span>
      )}
      {state === 'error' && (
        <span role="alert" style={{ width: '100%', color: 'var(--bad)', fontSize: 12 }}>{t('brainChat.failed', { defaultValue: copy.failed })}</span>
      )}
    </div>
  );
}

export default function BrainChipConfirm({ chips }) {
  const { t, locale } = useT();
  const [stored, setStored] = useState({});
  const ids = Array.isArray(chips) ? chips.map((c) => c.id).join(',') : '';
  useEffect(() => {
    if (!ids) return undefined;
    let cancelled = false;
    fetchBrainChipStatuses(ids.split(','))
      .then((map) => { if (!cancelled) setStored(map); })
      .catch((err) => logAppError({ type: 'brain_chip_status_error', message: err?.message || String(err), view: 'AgentShell', metadata: { step: 'brain_chip_status' } }));
    return () => { cancelled = true; };
  }, [ids]);
  if (!Array.isArray(chips) || chips.length === 0) return null;
  const copy = COPY[locale] || COPY.en;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="agent-brainchips">
      {chips.map((chip) => <ChipRow key={chip.id} chip={chip} stored={stored[chip.id]} copy={copy} t={t} />)}
    </div>
  );
}
